// store global types
import type { TCreateSlice } from "@/store";

// dependencies types
import type { TApiODIN } from "@/apis/odin/apiODIN";

type TResourceKey = "extraction-mapping" | "coa-remapping";

type TStateConcurrentUsers = {
    showConcurrentUserBanner: boolean;
    concurrentUsers: {
        email: string;
    }[];
};
type TActionsPublicConcurrentUsers = {
    dismissConcurrentUserBanner: () => void;
    startScreenSession: (params: {
        extractionId: number;
        resourceKey: TResourceKey;
    }) => Promise<void>;
    cleanUpExtractionScreenSession: () => void;
};

export type TSliceConcurrentUsers = TStateConcurrentUsers &
    TActionsPublicConcurrentUsers;

type TSliceDependenciesConcurrentUsers = {
    apiODIN: Pick<TApiODIN, "startScreenSession">;
};

let cleanUpExtractionScreenSessionRef: (() => void) | undefined;

export const initialStateConcurrentUsers = {
    showConcurrentUserBanner: true,
    concurrentUsers: [] as { email: string }[],
} as const satisfies TStateConcurrentUsers;

export const createSliceConcurrentUsers: TCreateSlice<
    TSliceConcurrentUsers,
    TSliceDependenciesConcurrentUsers
> =
    ({ apiODIN }) =>
    set => {
        const actionsPublic: TActionsPublicConcurrentUsers = {
            dismissConcurrentUserBanner: () => {
                set(
                    state => ({ ...state, showConcurrentUserBanner: false }),
                    false,
                    "concurrentUsers/dismissConcurrentUserBanner",
                );
            },
            startScreenSession: async ({
                extractionId,
                resourceKey,
            }: {
                extractionId: number;
                resourceKey: TResourceKey;
            }) => {
                const cleanup = await apiODIN.startScreenSession({
                    resourceKey,
                    extractionId,
                    onConnectionOpened: () => {
                        set(
                            {
                                concurrentUsers: [],
                                showConcurrentUserBanner: true,
                            },
                            false,
                            "concurrentUsers/startScreenSession/connectionOpened",
                        );
                    },
                    onActive({ activeUsers }) {
                        set(
                            {
                                concurrentUsers: activeUsers.map(user => ({
                                    email: user,
                                })),
                            },
                            false,
                            "concurrentUsers/startScreenSession/activeUsers",
                        );
                    },
                    onConnectionClosed() {
                        set(
                            {
                                concurrentUsers: [],
                                showConcurrentUserBanner: false,
                            },
                            false,
                            "concurrentUsers/startScreenSession/connectionClosed",
                        );
                    },
                });
                cleanUpExtractionScreenSessionRef = cleanup;
            },
            cleanUpExtractionScreenSession: () => {
                if (cleanUpExtractionScreenSessionRef) {
                    cleanUpExtractionScreenSessionRef();
                }
                cleanUpExtractionScreenSessionRef = undefined;
            },
        };

        return {
            ...initialStateConcurrentUsers,
            ...actionsPublic,
        };
    };
